import { useState, useEffect, useCallback } from 'react'
import { pdf } from '@react-pdf/renderer'
import { supabase } from '../../../api/supabase'
import { readExcelRows } from '../../../api/parseCommercialInvoiceFile'
import type { PricingRateCard, PricingCategory, PricingLane, PricingChargeCode, RateLineUnit } from './types'
import { RATE_LINE_UNIT_OPTIONS } from './types'
import RateCardPDF from './RateCardPDF'
import { downloadBlob, downloadRateCardCsv, downloadRateCardsXlsx, type ExportLineRow } from './pricingExport'

const inputCls = 'w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white'

interface ContainerType { id: string; code: string }

// The importer is deliberately a manual column-mapper, not an auto-detecting parser tuned to
// any specific spreadsheet layout -- the 5 real pricing xlsx files Gary referenced aren't
// present anywhere in this repo/session (checked imports/, test/fixtures/, everywhere), so
// there's nothing real to auto-detect against yet. Gary's own spec flagged this same risk
// ("可能需要人工映射 template"). Once real files are available this can grow real
// auto-detection on top of the same mapping fields.
type TargetField = 'charge_code' | 'unit' | 'container_type' | 'unit_price' | 'unit_cost' | 'min_charge' | 'max_charge' | 'notes'

const TARGET_FIELDS: { key: TargetField; label: string; required: boolean }[] = [
  { key: 'charge_code', label: 'Charge Code', required: true },
  { key: 'unit', label: 'Unit', required: true },
  { key: 'container_type', label: 'Container Type', required: false },
  { key: 'unit_price', label: 'Unit Price', required: true },
  { key: 'unit_cost', label: 'Unit Cost (internal)', required: false },
  { key: 'min_charge', label: 'Min Charge', required: false },
  { key: 'max_charge', label: 'Max Charge', required: false },
  { key: 'notes', label: 'Notes', required: false },
]

interface PreviewRow {
  raw: unknown[]
  chargeCodeId: string | null
  unit: RateLineUnit | null
  containerTypeId: string | null
  unitPrice: number | null
  unitCost: number | null
  minCharge: number | null
  maxCharge: number | null
  notes: string
  errors: string[]
}

function guessColumn(headers: string[], keywords: string[]): number {
  const idx = headers.findIndex(h => keywords.some(kw => h.toLowerCase().includes(kw)))
  return idx
}

export default function ImportExportTab() {
  const [cards, setCards] = useState<PricingRateCard[]>([])
  const [categories, setCategories] = useState<PricingCategory[]>([])
  const [lanes, setLanes] = useState<PricingLane[]>([])
  const [chargeCodes, setChargeCodes] = useState<PricingChargeCode[]>([])
  const [containerTypes, setContainerTypes] = useState<ContainerType[]>([])

  // Import state
  const [importCardId, setImportCardId] = useState('')
  const [headers, setHeaders] = useState<string[]>([])
  const [dataRows, setDataRows] = useState<unknown[][]>([])
  const [colMap, setColMap] = useState<Partial<Record<TargetField, number>>>({})
  const [importing, setImporting] = useState(false)
  const [importResult, setImportResult] = useState<string | null>(null)
  const [importError, setImportError] = useState<string | null>(null)

  // Export state
  const [exportCardId, setExportCardId] = useState('')
  const [exporting, setExporting] = useState(false)

  const load = useCallback(async () => {
    const [{ data: c }, { data: cat }, { data: l }, { data: cc }, { data: ct }] = await Promise.all([
      supabase.from('pricing_rate_cards').select('*').order('rate_card_name'),
      supabase.from('pricing_categories').select('*').order('display_order'),
      supabase.from('pricing_lanes').select('*'),
      supabase.from('pricing_charge_codes').select('*').eq('is_active', true),
      supabase.from('container_types').select('id, code').eq('is_active', true),
    ])
    setCards((c as PricingRateCard[]) || [])
    setCategories((cat as PricingCategory[]) || [])
    setLanes((l as PricingLane[]) || [])
    setChargeCodes((cc as PricingChargeCode[]) || [])
    setContainerTypes((ct as ContainerType[]) || [])
  }, [])

  useEffect(() => { load() }, [load])

  const chargeCodeByCode = new Map(chargeCodes.map(c => [c.code.toUpperCase(), c]))
  const containerTypeByCode = new Map(containerTypes.map(c => [c.code.toUpperCase(), c]))
  const unitByValue = new Map(RATE_LINE_UNIT_OPTIONS.map(u => [u.toLowerCase(), u]))

  const handleFile = async (file: File) => {
    setImportError(null); setImportResult(null)
    try {
      const grid = await readExcelRows(file, { raw: true })
      if (grid.length === 0) { setImportError('File has no rows.'); return }
      const hdrRow = (grid[0] || []).map(h => String(h ?? '').trim())
      setHeaders(hdrRow)
      setDataRows(grid.slice(1).filter(r => r.some(c => c !== null && c !== undefined && String(c).trim() !== '')))
      // Best-effort auto-suggest by header keyword -- staff can always override any mapping.
      setColMap({
        charge_code: [guessColumn(hdrRow, ['charge code', 'charge_code', 'code'])].find(i => i >= 0),
        unit: [guessColumn(hdrRow, ['unit'])].find(i => i >= 0),
        container_type: [guessColumn(hdrRow, ['container'])].find(i => i >= 0),
        unit_price: [guessColumn(hdrRow, ['unit price', 'price', 'rate'])].find(i => i >= 0),
        unit_cost: [guessColumn(hdrRow, ['cost'])].find(i => i >= 0),
        min_charge: [guessColumn(hdrRow, ['min'])].find(i => i >= 0),
        max_charge: [guessColumn(hdrRow, ['max'])].find(i => i >= 0),
        notes: [guessColumn(hdrRow, ['note', 'remark'])].find(i => i >= 0),
      })
    } catch (err: any) {
      setImportError(err?.message || 'Failed to parse file')
    }
  }

  const num = (v: unknown): number | null => {
    if (v === null || v === undefined || v === '') return null
    const n = typeof v === 'number' ? v : parseFloat(String(v).replace(/[£$,]/g, ''))
    return isNaN(n) ? null : n
  }

  const preview: PreviewRow[] = dataRows.map(row => {
    const get = (f: TargetField) => (colMap[f] !== undefined ? row[colMap[f]!] : undefined)
    const errors: string[] = []

    const codeRaw = String(get('charge_code') ?? '').trim().toUpperCase()
    const chargeCode = chargeCodeByCode.get(codeRaw)
    if (!codeRaw) errors.push('Charge Code missing')
    else if (!chargeCode) errors.push(`Unknown Charge Code "${codeRaw}"`)

    const unitRaw = String(get('unit') ?? '').trim().toLowerCase()
    const unit = unitByValue.get(unitRaw)
    if (!unitRaw) errors.push('Unit missing')
    else if (!unit) errors.push(`Unknown Unit "${unitRaw}"`)

    const ctRaw = String(get('container_type') ?? '').trim().toUpperCase()
    const containerType = ctRaw ? containerTypeByCode.get(ctRaw) : undefined
    if (ctRaw && !containerType) errors.push(`Unknown Container Type "${ctRaw}"`)

    const unitPrice = num(get('unit_price'))
    if (unitPrice === null) errors.push('Unit Price missing/invalid')

    return {
      raw: row,
      chargeCodeId: chargeCode?.id ?? null,
      unit: unit ?? null,
      containerTypeId: containerType?.id ?? null,
      unitPrice,
      unitCost: num(get('unit_cost')),
      minCharge: num(get('min_charge')),
      maxCharge: num(get('max_charge')),
      notes: String(get('notes') ?? ''),
      errors,
    }
  })

  const validRows = preview.filter(r => r.errors.length === 0)

  const runImport = async () => {
    if (!importCardId || validRows.length === 0) return
    setImporting(true); setImportError(null); setImportResult(null)
    try {
      const inserted = await supabase.from('pricing_rate_lines').insert(
        validRows.map(r => ({
          rate_card_id: importCardId, charge_code_id: r.chargeCodeId, unit: r.unit,
          container_type_id: r.containerTypeId, unit_price: r.unitPrice, min_charge: r.minCharge,
          max_charge: r.maxCharge, notes: r.notes || null,
        }))
      ).select('id')
      if (inserted.error) throw inserted.error
      const newIds = (inserted.data || []).map(r => r.id)
      const costsToWrite = validRows.map((r, i) => r.unitCost != null ? { rate_line_id: newIds[i], unit_cost: r.unitCost } : null).filter((c): c is { rate_line_id: string; unit_cost: number } => !!c)
      if (costsToWrite.length > 0) await supabase.from('pricing_rate_line_costs').insert(costsToWrite)
      setImportResult(`Imported ${validRows.length} line${validRows.length !== 1 ? 's' : ''}.`)
      setHeaders([]); setDataRows([]); setColMap({})
    } catch (err: any) {
      setImportError(err?.message || 'Import failed')
    } finally {
      setImporting(false)
    }
  }

  const loadCardExportData = async (cardId: string) => {
    const card = cards.find(c => c.id === cardId)
    if (!card) return null
    const { data: lineRows } = await supabase.from('pricing_rate_lines').select('*').eq('rate_card_id', cardId)
    const lines = (lineRows as any[]) || []
    const lineIds = lines.map(l => l.id)
    const { data: costRows } = lineIds.length > 0
      ? await supabase.from('pricing_rate_line_costs').select('*').in('rate_line_id', lineIds)
      : { data: [] as any[] }
    const costByLine = new Map((costRows || []).map((c: any) => [c.rate_line_id, c.unit_cost]))
    const exportLines: ExportLineRow[] = lines.map(l => {
      const cc = chargeCodes.find(c => c.id === l.charge_code_id)
      const ct = containerTypes.find(c => c.id === l.container_type_id)
      return {
        chargeCode: cc?.code || '', description: cc?.name_en || '', unit: l.unit,
        containerType: ct?.code || '', unitPrice: l.unit_price, unitCost: costByLine.get(l.id) ?? null,
        minCharge: l.min_charge, maxCharge: l.max_charge, notes: l.notes || '',
      }
    })
    return { card, exportLines }
  }

  const exportCsv = async () => {
    if (!exportCardId) return
    setExporting(true)
    try {
      const result = await loadCardExportData(exportCardId)
      if (!result) return
      downloadRateCardCsv(`${result.card.rate_card_name.replace(/[^a-z0-9]+/gi, '_')}.csv`, result.card.rate_card_name, result.exportLines)
    } finally {
      setExporting(false)
    }
  }

  const exportPdf = async () => {
    if (!exportCardId) return
    setExporting(true)
    try {
      const result = await loadCardExportData(exportCardId)
      if (!result) return
      const { card, exportLines } = result
      const category = categories.find(c => c.id === card.mode_id)
      const lane = lanes.find(l => l.id === card.lane_id)
      const blob = await pdf(
        <RateCardPDF
          rateCardName={card.rate_card_name}
          customerName={null}
          modeName={category?.name_en || '—'}
          laneName={lane?.name || null}
          effectiveFrom={card.effective_from}
          effectiveTo={card.effective_to}
          currency={card.currency}
          lines={exportLines.map(l => ({ chargeCode: l.chargeCode, description: l.description, unit: l.unit, unitPrice: l.unitPrice }))}
        />
      ).toBlob()
      downloadBlob(`${card.rate_card_name.replace(/[^a-z0-9]+/gi, '_')}.pdf`, blob, 'application/pdf')
    } finally {
      setExporting(false)
    }
  }

  const exportXlsxAll = async () => {
    setExporting(true)
    try {
      const sheets = []
      for (const card of cards) {
        const result = await loadCardExportData(card.id)
        if (result) sheets.push({ sheetName: result.card.rate_card_name, lines: result.exportLines })
      }
      downloadRateCardsXlsx(`pricing_rate_cards_${new Date().toISOString().slice(0, 10)}.xlsx`, sheets)
    } finally {
      setExporting(false)
    }
  }

  return (
    <div className="space-y-8">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-6">
        <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-1 uppercase tracking-wide">Import Excel</h3>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
          Bulk-add line items to an existing rate card from a spreadsheet. Map your columns below -- this doesn't try to auto-detect a specific layout.
        </p>
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Target Rate Card *</label>
          <select className={inputCls} value={importCardId} onChange={e => setImportCardId(e.target.value)}>
            <option value="">— Select a rate card —</option>
            {cards.map(c => <option key={c.id} value={c.id}>{c.rate_card_name}</option>)}
          </select>
        </div>
        <div className="mb-4">
          <input type="file" accept=".xlsx,.xls,.csv" disabled={!importCardId}
            onChange={e => e.target.files?.[0] && handleFile(e.target.files[0])}
            className="text-sm text-gray-700 dark:text-gray-300" />
        </div>
        {importError && <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-lg text-sm">{importError}</div>}
        {importResult && <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 rounded-lg text-sm">{importResult}</div>}

        {headers.length > 0 && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              {TARGET_FIELDS.map(f => (
                <div key={f.key}>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">{f.label}{f.required ? ' *' : ''}</label>
                  <select className={inputCls} value={colMap[f.key] ?? ''} onChange={e => setColMap(m => ({ ...m, [f.key]: e.target.value === '' ? undefined : parseInt(e.target.value, 10) }))}>
                    <option value="">— not mapped —</option>
                    {headers.map((h, i) => <option key={i} value={i}>{h || `Column ${i + 1}`}</option>)}
                  </select>
                </div>
              ))}
            </div>

            <div className="overflow-x-auto max-h-80 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg mb-4">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0">
                  <tr>
                    <th className="px-2 py-2 text-left">Charge Code</th>
                    <th className="px-2 py-2 text-left">Unit</th>
                    <th className="px-2 py-2 text-left">Container</th>
                    <th className="px-2 py-2 text-left">Price</th>
                    <th className="px-2 py-2 text-left">Cost</th>
                    <th className="px-2 py-2 text-left">Errors</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {preview.map((r, i) => (
                    <tr key={i} className={r.errors.length > 0 ? 'bg-red-50 dark:bg-red-900/10' : ''}>
                      <td className="px-2 py-1.5">{chargeCodes.find(c => c.id === r.chargeCodeId)?.code || '—'}</td>
                      <td className="px-2 py-1.5">{r.unit || '—'}</td>
                      <td className="px-2 py-1.5">{containerTypes.find(c => c.id === r.containerTypeId)?.code || '—'}</td>
                      <td className="px-2 py-1.5">{r.unitPrice ?? '—'}</td>
                      <td className="px-2 py-1.5">{r.unitCost ?? '—'}</td>
                      <td className="px-2 py-1.5 text-red-600">{r.errors.join('; ')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex items-center gap-4">
              <button onClick={runImport} disabled={importing || validRows.length === 0} className="px-5 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
                {importing ? 'Importing...' : `Import ${validRows.length} Valid Row${validRows.length !== 1 ? 's' : ''}`}
              </button>
              <span className="text-xs text-gray-400">{preview.length - validRows.length} row(s) with errors will be skipped.</span>
            </div>
          </>
        )}
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-6">
        <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-1 uppercase tracking-wide">Export</h3>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">CSV/PDF are customer-safe (no cost). Excel export includes internal cost -- for staff use only.</p>
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Rate Card</label>
          <select className={inputCls} value={exportCardId} onChange={e => setExportCardId(e.target.value)}>
            <option value="">— Select a rate card —</option>
            {cards.map(c => <option key={c.id} value={c.id}>{c.rate_card_name}</option>)}
          </select>
        </div>
        <div className="flex flex-wrap gap-3">
          <button onClick={exportCsv} disabled={!exportCardId || exporting} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50">
            Export CSV (customer)
          </button>
          <button onClick={exportPdf} disabled={!exportCardId || exporting} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50">
            Export PDF (customer)
          </button>
          <button onClick={exportXlsxAll} disabled={exporting || cards.length === 0} className="px-4 py-2 bg-gray-700 text-white rounded-lg text-sm font-medium hover:bg-gray-800 disabled:opacity-50">
            Export All to Excel (internal, incl. cost)
          </button>
        </div>
      </div>
    </div>
  )
}
